import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'
import {
  OnboardingStepType,
  OrganizationDetailFragment,
  usePayrollInstructionsCompleteMutation,
  useStartOnboardingStep,
} from '@alice-financial/alice-sdk'
import { useMutationOptionsWithInvalidation } from '@alice-financial/api'
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  ExternalLink,
  Grid,
  HealthcheckItem,
  Typography,
} from '@alice-financial/pretext-ui'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { getServerErrorHandler } from '../../../utils/forms/ServerError'
import { useCurrentOrgRootPath, useOrg } from '../organization/useOrg'
import { usePayrollConnectionStatus } from './usePayrollConnectionStatus'

type PayrollInstructionsCompleteProps = ButtonProps & {
  orgId: number
}
export const PayrollInstructionsCompleteButton = ({
  orgId,
  ...buttonProps
}: PayrollInstructionsCompleteProps) => {
  const { setError } = useForm()
  const onError = getServerErrorHandler(setError)
  const mutationOptions = useMutationOptionsWithInvalidation({ onError }, [useOrg.getKey({ orgId })])
  const {
    mutate: payrollInstructionsComplete,
    isLoading,
    isSuccess,
  } = usePayrollInstructionsCompleteMutation(mutationOptions)

  const handleClick = () => payrollInstructionsComplete({ input: { orgId } })

  return (
    <Button
      onClick={handleClick}
      variant="contained"
      disabled={isLoading || isSuccess}
      {...buttonProps}
      sx={{ my: 1 }}
    >
      <FormattedMessage id="organization.onboarding.payroll_instructions_complete" />
    </Button>
  )
}

const DEFAULT_PAYROLL_ARTICLE_LINK =
  'https://help.thisisalice.com/article/245-connecting-other-platform-and-alice'
const PAYROLL_ARTICLE_LINKS: Record<string, string | null> = {
  accupay: null,
  adp: null,
  adp_workforce: 'https://help.thisisalice.com/article/266-connecting-adp-wfn-and-alice',
  adp_resource: null,
  run_adp: 'https://help.thisisalice.com/article/206-adp-run-setup-with-alice',
  'adp-totalsource': null,
  alliance: 'https://help.thisisalice.com/article/229-alliance-setup-with-alice',
  alliance_automated: 'https://help.thisisalice.com/article/229-alliance-setup-with-alice',
  aps: null,
  benefit_mall_pay_focus: null,
  'BenefitMall Pro': null,
  ceridian: null,
  cfs: null,
  compupay: null,
  payday_hcm: null,
  exact: null,
  gusto: 'https://help.thisisalice.com/article/209-gusto-setup-with-alice',
  heartland: null,
  heartland_payroll: null,
  intuit_online_payroll: null,
  justworks: null,
  kronos_workforce_ready: null,
  namely: null,
  on_pay: null,
  paychex: 'https://help.thisisalice.com/article/231-paychex-updated-integration',
  paychex_automated: 'https://help.thisisalice.com/article/231-paychex-updated-integration',
  paychex_flex: 'https://help.thisisalice.com/article/231-paychex-updated-integration',
  paychex_offline: null,
  paycom: 'https://help.thisisalice.com/article/230-paycom-setup-with-alice',
  paycor: 'https://help.thisisalice.com/article/252-connecting-paycor-and-alice',
  paycor_automated: 'https://help.thisisalice.com/article/252-connecting-paycor-and-alice',
  paylocity: 'https://help.thisisalice.com/article/211-paylocity-setup-with-alice',
  paylocity_automated: 'https://help.thisisalice.com/article/211-paylocity-setup-with-alice',
  paynorthwest: null,
  Payright: null,
  payrollontime: null,
  payroll_solutions: null,
  payville: null,
  Payville_USA: null,
  PlatinumPay: null,
  Premier_Payroll_Solutions: null,
  prg: null,
  primepay: null,
  prismhr: null,
  profit_builders: null,
  quickbooks_box: null,
  quickbooks_online: null,
  R365: null,
  rippling: 'https://help.thisisalice.com/article/225-rippling-setup-with-alice',
  rsi: null,
  sample: null,
  sdp: null,
  spur: null,
  sure_payroll: null,
  test: null,
  TimePays: null,
  stratex: 'https://help.thisisalice.com/article/202-toast-deductions-earnings-set-up',
  toast: 'https://help.thisisalice.com/article/202-toast-deductions-earnings-set-up',
  trax: null,
  ukg_ready: null,
  ultipro: null,
  ultipro_automated: null,
  valiant: null,
  Valiant_Trackforce: null,
  viventium: null,
  wells_fargo_direct_pay: null,
  xero: null,
  zenefits: null,
  other: null,
}

export const PayrollConnectionInputs = () => {
  const intl = useIntl()
  const org = useOrg()
  const { mutate: startOnboardingStep } = useStartOnboardingStep(org?.id || 0)
  if (!org) return null
  const payrollPlatform = org.payrollConnection.payrollPlatform
  const isOnboarded = Boolean(org.isOnboarded)
  const isDoneWithPayrollInstructions =
    isOnboarded || Boolean(org.payrollConnection.payrollInstructionsCompletedAt)
  const isPayrollConnected = isOnboarded || Boolean(org.payrollConnection.payrollConnectedAt)
  if (!payrollPlatform) {
    return (
      <Typography variant="body2" gutterBottom>
        <strong>Payroll platform not found</strong>
      </Typography>
    )
  }
  if (!isDoneWithPayrollInstructions) {
    return (
      <>
        <Typography variant="body2" gutterBottom>
          This allows Alice to record deductions and reimbursements on payroll for enrolled employees.
        </Typography>
        <Grid container spacing={2} flexWrap="nowrap">
          <Grid>
            <ArrowCircleRightIcon fontSize="large" color="primary" />
          </Grid>
          <Grid py={1}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Follow our instructions for{' '}
              <ExternalLink
                href={PAYROLL_ARTICLE_LINKS[payrollPlatform.code] || DEFAULT_PAYROLL_ARTICLE_LINK}
                target="_blank"
                onClick={() => startOnboardingStep({ stepType: OnboardingStepType.ConnectPayroll })}
              >
                setting up the Alice admin account
              </ExternalLink>{' '}
              in {payrollPlatform.name}.
            </Typography>
            <Typography variant="body2">
              When you&apos;re done, click &apos;
              {intl.formatMessage({ id: 'organization.onboarding.payroll_instructions_complete' })}&apos;.
            </Typography>
            <PayrollInstructionsCompleteButton orgId={org.id} variant="contained" color="primary" />
          </Grid>
        </Grid>
      </>
    )
  }
  if (!isPayrollConnected) {
    return (
      <Box>
        {payrollPlatform.code !== 'premier_payroll_solutions' && (
          <Typography variant="body2" gutterBottom>
            You have let us know that you have set up the Alice payroll account following the instructions{' '}
            <ExternalLink
              href={PAYROLL_ARTICLE_LINKS[payrollPlatform.code] || DEFAULT_PAYROLL_ARTICLE_LINK}
              target="_blank"
            >
              here
            </ExternalLink>
            .
          </Typography>
        )}

        <Alert severity="info" icon={<AccessTimeIcon />} sx={{ my: 2 }}>
          We&apos;ll email you within 1 business day if we need anything more to confirm payroll setup.
        </Alert>
      </Box>
    )
  }
  return <Typography variant="body2">We&apos;ve verified the account and confirmed admin access.</Typography>
}

export const PayrollConnectionHealthcheck = ({
  org,
  todo,
}: {
  org: OrganizationDetailFragment
  todo?: boolean
}) => {
  const currentOrgRootPath = useCurrentOrgRootPath()
  const statusResult = usePayrollConnectionStatus(org)

  return (
    <HealthcheckItem
      to={`${currentOrgRootPath}/payroll/write-access`}
      status={statusResult.status}
      primary="Payroll admin access"
      secondary={statusResult.secondary}
      todo={todo || statusResult.status !== 'complete'}
    >
      <PayrollConnectionInputs />
    </HealthcheckItem>
  )
}
